import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { AddTokenForm } from '../components/AddTokenForm';
import { updatePersonalConfiguration } from '../services/api';
import { getErrorMessage } from '../services/helpers';
import { showFlag } from '../services/jira';
import { PersonalConfiguration } from '../types';
import { useDatabaseContext } from './DatabaseContextProvider';

interface TogglAccountContextData {
  token: string;
  removeToken: () => Promise<void>;
}

const TogglAccountContext = React.createContext<
  TogglAccountContextData | undefined
>(undefined);

export function TogglAccountTokenProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    data: { personalConfiguration },
    updateContext,
  } = useDatabaseContext();

  useEffect(() => {
    if (personalConfiguration.isTokenRevokedBySynchronizer) {
      showFlag(
        'Toggl API Access Token No Longer Valid',
        'Your access token has been revoked or expired. Please update it to continue using the service.',
        'error'
      );
    }
  }, [personalConfiguration.isTokenRevokedBySynchronizer]);

  const setToken = useCallback(
    (togglApiToken: string | undefined) => {
      const setPersonalConfig = updateContext('personalConfiguration');
      setPersonalConfig((prev) => ({
        ...(prev as PersonalConfiguration),
        togglApiToken,
      }));
    },
    [updateContext]
  );

  const onSubmit = useCallback(async (togglApiToken: string) => {
    try {
      await updatePersonalConfiguration({
        togglApiToken,
        isTokenRevokedBySynchronizer: null,
      });
      setToken(togglApiToken);
    } catch (e) {
      console.error(e);
      return Promise.resolve({ token: true });
    }
  }, []);

  const removeToken = useCallback(async () => {
    try {
      await updatePersonalConfiguration({ togglApiToken: null });
      setToken(undefined);
    } catch (error) {
      console.error(
        'Error while removing Toggl account token',
        getErrorMessage(error)
      );
      showFlag(
        'Error while removing Toggl account token',
        getErrorMessage(error),
        'error'
      );
    }
  }, []);

  const context = useMemo(
    () => ({
      token: personalConfiguration.togglApiToken!,
      removeToken,
    }),
    [personalConfiguration.togglApiToken, removeToken]
  );

  if (!personalConfiguration.togglApiToken) {
    return <AddTokenForm onSubmit={onSubmit} />;
  } else {
    return (
      <TogglAccountContext.Provider value={context}>
        {children}
      </TogglAccountContext.Provider>
    );
  }
}

export function useTogglAccountContext() {
  const context = useContext(TogglAccountContext);
  if (!context) {
    throw new Error(
      'Toggl context not initialized, probably context provider is missing'
    );
  }
  return context;
}
